import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
  Fade
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Person as PersonIcon,
  Build as ServiceIcon,
  VpnKey as KeyIcon
} from '@mui/icons-material';
import useAppStore from '../store/useAppStore';
import { employeeAPI } from '../services/apiWithAuth';

const EmployeeManager = () => {
  const { 
    employees, 
    loadingEmployees,
    setEmployees, 
    setLoadingEmployees,
    addEmployee,
    updateEmployee,
    removeEmployee,
    setNotification 
  } = useAppStore();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    email: '',
    is_active: true,
    is_service: false,
    has_keys: false,
    order_position: 0
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Load employees on mount
  useEffect(() => {
    loadEmployees();
  }, []);
  
  const loadEmployees = async () => {
    // Nie pokazuj loadingu jeśli są już jakieś dane w cache
    if (employees.length === 0) {
      setLoadingEmployees(true);
    }
    
    try {
      const data = await employeeAPI.getAll();
      setEmployees(data);
    } catch (error) {
      console.error('Error loading employees:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas ładowania pracowników'
      });
    } finally {
      setLoadingEmployees(false);
    }
  };
  
  const handleOpenDialog = (employee = null) => {
    setEditingEmployee(employee);
    setFormData(employee ? {
      code: employee.code,
      name: employee.name || '',
      email: employee.email || '',
      is_active: employee.is_active,
      is_service: employee.is_service || false,
      has_keys: employee.has_keys || false,
      order_position: employee.order_position
    } : {
      code: '',
      name: '',
      email: '',
      is_active: true,
      is_service: false,
      has_keys: false,
      order_position: employees.length + 1
    });
    setFormErrors({});
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEmployee(null);
    setFormData({ code: '', name: '', email: '', is_active: true, is_service: false, has_keys: false, order_position: 0 });
    setFormErrors({});
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.code.trim()) {
      errors.code = 'Kod pracownika jest wymagany';
    } else if (formData.code.length < 1 || formData.code.length > 3) {
      errors.code = 'Kod musi mieć od 1 do 3 znaków';
    } else if (!/^[A-Z]+$/.test(formData.code)) {
      errors.code = 'Kod może zawierać tylko wielkie litery';
    } else if (!editingEmployee && employees.some(emp => emp.code === formData.code)) {
      errors.code = 'Kod pracownika już istnieje';
    }
    
    if (!formData.name.trim()) {
      errors.name = 'Nazwa pracownika jest wymagana';
    }
    
    // Walidacja email (opcjonalny, ale musi być poprawny jeśli podany)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        errors.email = 'Nieprawidłowy adres email';
      } else if (!editingEmployee && employees.some(emp => emp.email && emp.email.toLowerCase() === formData.email.toLowerCase())) {
        errors.email = 'Ten adres email jest już przypisany do innego pracownika';
      } else if (editingEmployee && employees.some(emp => emp.email && emp.email.toLowerCase() === formData.email.toLowerCase() && emp.code !== editingEmployee.code)) {
        errors.email = 'Ten adres email jest już przypisany do innego pracownika';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      if (editingEmployee) {
        // Update existing employee - remove code from data since it's in URL
        const { code, ...updateData } = formData;
        const updated = await employeeAPI.update(editingEmployee.code, updateData);
        updateEmployee(updated);
        setNotification({
          type: 'success',
          message: `Zaktualizowano pracownika ${formData.name}`
        });
      } else {
        // Create new employee - include code for creation
        const newEmployee = await employeeAPI.create(formData);
        addEmployee(newEmployee);
        setNotification({
          type: 'success',
          message: `Dodano pracownika ${formData.name}`
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving employee:', error);
      
      // Szczegółowe logowanie błędu
      let errorMessage = 'Błąd podczas zapisywania pracownika';
      
      if (error.response) {
        // Błąd HTTP z backendu
        console.error('HTTP Error:', error.response.status, error.response.data);
        
        if (error.response.status === 409) {
          errorMessage = `Pracownik z kodem '${formData.code}' już istnieje`;
        } else if (error.response.status === 422) {
          errorMessage = 'Nieprawidłowe dane pracownika';
          if (error.response.data.detail) {
            console.error('Validation errors:', error.response.data.detail);
            errorMessage += ': ' + JSON.stringify(error.response.data.detail);
          }
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        // Brak odpowiedzi z serwera
        console.error('No response from server:', error.request);
        errorMessage = 'Brak połączenia z serwerem';
      } else {
        // Inny błąd
        console.error('Error message:', error.message);
        errorMessage = error.message;
      }
      
      setNotification({
        type: 'error',
        message: errorMessage
      });
    }
  };
  
  const handleDelete = async (employee) => {
    if (window.confirm(`Czy na pewno chcesz usunąć pracownika ${employee.name}? Ta operacja jest nieodwracalna!`)) {
      try {
        await employeeAPI.delete(employee.code);
        // Backend usuwa pracownika z bazy danych, więc usuń go ze stanu
        removeEmployee(employee.code);
        setNotification({
          type: 'success',
          message: `Usunięto pracownika ${employee.name}`
        });
      } catch (error) {
        console.error('Error deleting employee:', error);
        setNotification({
          type: 'error',
          message: 'Błąd podczas usuwania pracownika'
        });
      }
    }
  };
  
  const handleToggleActive = async (employee) => {
    try {
      // Wysyłaj tylko pola dozwolone w schemacie EmployeeUpdate
      const updated = await employeeAPI.update(employee.code, {
        name: employee.name,
        is_active: !employee.is_active,
        is_service: employee.is_service,
        has_keys: employee.has_keys,
        order_position: employee.order_position
      });
      updateEmployee(updated);
      setNotification({
        type: 'success',
        message: `${updated.is_active ? 'Aktywowano' : 'Dezaktywowano'} pracownika ${employee.name}`
      });
    } catch (error) {
      console.error('Error toggling employee status:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas zmiany statusu pracownika'
      });
    }
  };
  
  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Pracownicy ({employees.length})
          </Typography>
          <Fade in={loadingEmployees}>
            <CircularProgress size={20} sx={{ ml: 1 }} />
          </Fade>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
        >
          Dodaj
        </Button>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <List dense>
        {employees.map((employee) => (
          <ListItem
            key={employee.code}
            sx={{
              border: 1,
              borderColor: employee.is_active ? 'grey.300' : 'grey.600',
              borderRadius: 1,
              mb: 1,
              backgroundColor: employee.is_active ? 'background.paper' : 'action.hover',
              opacity: employee.is_active ? 1 : 0.7
            }}
          >
            <DragIcon sx={{ mr: 1, color: 'grey.400' }} />
            
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={employee.code}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      opacity: employee.is_active ? 1 : 0.6,
                      textDecoration: employee.is_active ? 'none' : 'line-through'
                    }}
                  >
                    {employee.name || 'Bez nazwy'}
                  </Typography>
                  {employee.is_service && (
                    <Tooltip title="Pracownik serwisu">
                      <ServiceIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    </Tooltip>
                  )}
                  {employee.has_keys && (
                    <Tooltip title="Posiada klucze">
                      <KeyIcon sx={{ color: '#f9a825', fontSize: 20 }} />
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Pozycja: {employee.order_position} | 
                    Status: {employee.is_active ? 'Aktywny' : 'Nieaktywny'}
                    {employee.is_service && ' | Serwis'}
                  </Typography>
                  {employee.email && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Email: {employee.email}
                    </Typography>
                  )}
                </Box>
              }
            />
            
            <ListItemSecondaryAction>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={employee.is_active ? 'Dezaktywuj' : 'Aktywuj'}>
                  <Switch
                    checked={employee.is_active}
                    onChange={() => handleToggleActive(employee)}
                    size="small"
                  />
                </Tooltip>
                
                <Tooltip title="Edytuj">
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(employee)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Usuń">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(employee)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
        
        {employees.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography variant="body1">
              Brak pracowników
            </Typography>
            <Typography variant="body2">
              Kliknij "Dodaj" aby utworzyć pierwszego pracownika
            </Typography>
          </Box>
        )}
      </List>
      
      {/* Employee Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingEmployee ? 'Edytuj pracownika' : 'Dodaj pracownika'}
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Kod pracownika"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              error={!!formErrors.code}
              helperText={formErrors.code || 'Unikalny kod 1-3 wielkie litery (np. JK, PPS, KS)'}
              inputProps={{ maxLength: 3 }}
              disabled={!!editingEmployee}
            />
            
            <TextField
              fullWidth
              label="Nazwa pracownika"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={!!formErrors.name}
              helperText={formErrors.name || 'Imię i nazwisko pracownika'}
            />
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={!!formErrors.email}
              helperText={formErrors.email || 'Adres email (opcjonalny, dla autoryzacji Azure AD)'}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Pracownik aktywny"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_service}
                  onChange={(e) => setFormData({ ...formData, is_service: e.target.checked })}
                  color="error"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ServiceIcon fontSize="small" />
                  <span>Pracownik serwisu</span>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.has_keys}
                  onChange={(e) => setFormData({ ...formData, has_keys: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#f9a825',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#f9a825',
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <KeyIcon fontSize="small" sx={{ color: '#f9a825' }} />
                  <span>Posiada klucze</span>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anuluj</Button>
          <Button onClick={handleSave} variant="contained">
            {editingEmployee ? 'Zapisz' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EmployeeManager;